import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchMainCategories } from "../../store/slices/mainCategorySlice";
import ProductCard, { ProductCardSkeleton } from "./ProductCard";
import SidebarFilter from "./SidebarFilter";
import { FiPlus } from "react-icons/fi";

const FilteredProductShowcase = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { products, loading } = useSelector(
    (state: RootState) => state.product,
  );
  const { mainCategories } = useSelector(
    (state: RootState) => state.mainCategory,
  );
  const [sortBy, setSortBy] = useState("All");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState("All");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [activeFilters, setActiveFilters] = useState<any>({});

  useEffect(() => {
    if (mainCategories.length === 0) {
      dispatch(fetchMainCategories());
    }
  }, [dispatch, mainCategories.length]);

  useEffect(() => {
    const params: any = {};
    const filter: any = {};

    if (sortBy !== "All") {
      if (sortBy === "Featured") filter.is_featured = true;
      if (sortBy === "Newest Arrivals") filter.is_new = true;
    }

    // Apply category filter (multiple allowed)
    if (activeFilters.category_id && activeFilters.category_id.length > 0) {
      filter.category_id = { $in: activeFilters.category_id };
    } else if (
      activeFilters.main_category &&
      activeFilters.main_category.length > 0
    ) {
      // Apply main category filter (multiple allowed)
      filter.main_category = { $in: activeFilters.main_category };
    } else if (selectedMainCategory !== "All") {
      filter.main_category = selectedMainCategory;
    }

    // Apply availability filter
    if (activeFilters.availability && activeFilters.availability.length > 0) {
      const hasInStock = activeFilters.availability.includes("in-stock");
      const hasOutOfStock = activeFilters.availability.includes("out-of-stock");
      if (hasInStock && !hasOutOfStock) {
        filter.stock = { $gt: 0 };
      } else if (hasOutOfStock && !hasInStock) {
        filter.stock = 0;
      }
    }

    params.filter = filter;
    dispatch(fetchProducts(params));
  }, [sortBy, activeFilters, selectedMainCategory, dispatch]);

  const resultCount = products.length;

  const handleFilterChange = (filters: any) => {
    console.log("Filters updated:", filters);
    setActiveFilters(filters);
  };

  const selectedMainCategoryName =
    selectedMainCategory === "All"
      ? "All Categories"
      : mainCategories.find((mc) => mc._id === selectedMainCategory)?.name ||
        "All Categories";

  return (
    <div className="showcase-root">
      <style>
        {`
                .showcase-root {
                    padding: 0;
                    background-color: #fff;
                    width: 100%;
                    box-sizing: border-box;
                }
                .showcase-container {
                    max-width: 1580px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: row;
                    width: 100%;
                    box-sizing: border-box;
                }
                .showcase-main-content {
                    flex: 1;
                    padding: 30px 40px;
                    box-sizing: border-box;
                }
                .showcase-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    margin-bottom: 30px;
                }
                .showcase-title {
                    font-size: 32px;
                    font-weight: 300;
                    color: #222;
                    font-family: serif;
                    margin: 0 0 8px 0;
                }
                .showcase-results {
                    font-size: 12px;
                    color: #888;
                    text-transform: uppercase;
                }
                .showcase-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 30px 20px;
                }
                .mobile-categories-dropdown {
                    display: none;
                }
                .showcase-controls {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }

                @media (max-width: 900px) {
                    .showcase-container {
                        flex-direction: column !important;
                    }
                    .showcase-main-content {
                        padding: 20px 16px !important;
                    }
                    .showcase-header {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        margin-bottom: 20px !important;
                        gap: 16px !important;
                    }
                    .showcase-title {
                        font-size: 24px !important;
                        margin-bottom: 4px !important;
                    }
                    .showcase-grid {
                        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important;
                        gap: 20px 12px !important;
                    }
                    .mobile-categories-dropdown {
                        display: block !important;
                    }
                    .showcase-controls {
                        width: 100% !important;
                        justify-content: space-between !important;
                    }
                    .showcase-controls > div {
                        flex: 1 !important;
                    }
                }

                @media (max-width: 480px) {
                    .showcase-main-content {
                        padding: 16px 12px !important;
                    }
                    .showcase-title {
                        font-size: 20px !important;
                    }
                    .showcase-grid {
                        grid-template-columns: repeat(auto-fill, minmax(145px, 1fr)) !important;
                        gap: 16px 10px !important;
                    }
                }
                `}
      </style>
      <div className="showcase-container">
        {/* Side Filter */}
        <SidebarFilter onFilterChange={handleFilterChange} />

        {/* Main Content Area */}
        <div className="showcase-main-content">
          {/* Header row */}
          <div className="showcase-header">
            <div>
              <h1 className="showcase-title">All Products</h1>
              <span className="showcase-results">
                {loading ? "Calculating..." : `${resultCount} Results`}
              </span>
            </div>

            <div className="showcase-controls">
              {/* All Categories Dropdown (Visible on Mobile/Tablet) */}
              <div
                className="mobile-categories-dropdown"
                style={{ position: "relative" }}
              >
                <div
                  onClick={() => {
                    setIsCategoryOpen(!isCategoryOpen);
                    setIsSortOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    border: "1px solid #eee",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    Category :
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                    }}
                  >
                    {selectedMainCategoryName}
                  </span>
                  <FiPlus
                    size={14}
                    style={{
                      transform: isCategoryOpen ? "rotate(45deg)" : "none",
                      transition: "transform 0.3s ease",
                      marginLeft: "4px",
                    }}
                  />
                </div>

                {isCategoryOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #eee",
                      borderRadius: "2px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      zIndex: 10,
                      width: "180px",
                      marginTop: "4px",
                      maxHeight: "250px",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      onClick={() => {
                        setSelectedMainCategory("All");
                        setIsCategoryOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        cursor: "pointer",
                        backgroundColor:
                          selectedMainCategory === "All"
                            ? "#f5f5f5"
                            : "transparent",
                      }}
                    >
                      All Categories
                    </div>
                    {[...(mainCategories || [])]
                      .sort(
                        (a: any, b: any) =>
                          (Number(a.priority) || 999) -
                          (Number(b.priority) || 999),
                      )
                      .map((mc) => (
                      <div
                        key={mc._id}
                        onClick={() => {
                          setSelectedMainCategory(mc._id || "");
                          setIsCategoryOpen(false);
                        }}
                        style={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          cursor: "pointer",
                          borderTop: "1px solid #eee",
                          backgroundColor:
                            selectedMainCategory === mc._id
                              ? "#f5f5f5"
                              : "transparent",
                        }}
                      >
                        {mc.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort By Dropdown */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => {
                    setIsSortOpen(!isSortOpen);
                    setIsCategoryOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    border: "1px solid #eee",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    Sort By :
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#333",
                    }}
                  >
                    {sortBy}
                  </span>
                  <FiPlus
                    size={14}
                    style={{
                      transform: isSortOpen ? "rotate(45deg)" : "none",
                      transition: "transform 0.3s ease",
                      marginLeft: "4px",
                    }}
                  />
                </div>

                {isSortOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #eee",
                      borderRadius: "2px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      zIndex: 10,
                      width: "180px",
                      marginTop: "4px",
                    }}
                  >
                    <div
                      onClick={() => {
                        setSortBy("All");
                        setIsSortOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        cursor: "pointer",
                        backgroundColor:
                          sortBy === "All" ? "#f5f5f5" : "transparent",
                      }}
                    >
                      All
                    </div>
                    <div
                      onClick={() => {
                        setSortBy("Newest Arrivals");
                        setIsSortOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        cursor: "pointer",
                        borderTop: "1px solid #eee",
                        backgroundColor:
                          sortBy === "Newest Arrivals"
                            ? "#f5f5f5"
                            : "transparent",
                      }}
                    >
                      Newest Arrivals
                    </div>
                    <div
                      onClick={() => {
                        setSortBy("Featured");
                        setIsSortOpen(false);
                      }}
                      style={{
                        padding: "12px 16px",
                        fontSize: "14px",
                        cursor: "pointer",
                        borderTop: "1px solid #eee",
                        backgroundColor:
                          sortBy === "Featured" ? "#f5f5f5" : "transparent",
                      }}
                    >
                      Featured
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="showcase-grid">
            {loading ? (
              Array(8)
                .fill(0)
                .map((_, i) => <ProductCardSkeleton key={i} />)
            ) : products.length === 0 ? (
              <div
                style={{
                  gridColumn: "1/-1",
                  textAlign: "center",
                  padding: "40px",
                  color: "#666",
                }}
              >
                No products found matching your criteria.
              </div>
            ) : (
              products?.map((product) => {
                const off = Math.round(
                  ((product.mrp - product.price) / product.mrp) * 100,
                );
                return (
                  <ProductCard
                    key={product._id}
                    _id={product._id}
                    name={product.name}
                    price={product.price}
                    originalPrice={product.mrp}
                    off={`${off}%`}
                    star={4.5}
                    image={
                      product.images && product.images.length > 0
                        ? typeof product.images[0] === "string"
                          ? product.images[0]
                          : (product.images[0] as any)?.url || ""
                        : ""
                    }
                    images={
                      product.images &&
                      product.images
                        .map((img) =>
                          typeof img === "string" ? img : (img as any)?.url,
                        )
                        .filter(Boolean)
                    }
                    is_featured={product.is_featured}
                    is_new={product.is_new}
                    emi={`₹${Math.round(product.price / 12)}`}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilteredProductShowcase;
